/* WM-06 shared option-image lifecycle. Catalog owns artwork; IO/native controllers own
   field values, availability, prices, personalization and commerce properties. */
(() => {
  'use strict';
  const configNode = document.getElementById('pr-product-options-config');
  const catalog = window.PR_OPTION_IMAGES;
  if (!configNode || !catalog || configNode.dataset.prImagesStarted) return;
  let config;
  try { config = JSON.parse(configNode.textContent); } catch (_) { return; }
  if (config.version !== 1 || !Array.isArray(config.roles)) return;
  configNode.dataset.prImagesStarted = 'true';
  const valueAt = path => path.split('.').reduce((obj, key) => obj?.[key], catalog);
  function canon(value, basic) {
    let text = (value || '').replace(/[’‘‛`´]/g, "'");
    if (!basic) text = text.replace(/\u00AE|\u2122/gi, '').replace(/\((r|tm)\)/gi, '');
    text = text.replace(/\s+/g, ' ');
    if (!basic) text = text.replace(/:\s*$/, '');
    return text.trim().toLowerCase();
  }
  const roles = config.roles.map(role => ({ ...role, lookup: Object.fromEntries(
    Object.entries(catalog[role.map] || {}).map(([key, url]) => [canon(key, role.basic), url])
  ) }));
  const style = {
    position: 'absolute', top: '12px', left: '12px', width: '72px', height: '72px',
    objectFit: 'cover', border: '1px solid #e8d7b0', borderRadius: '6px',
    boxShadow: '0 1px 0 rgba(0,0,0,.02)'
  };
  function matchesLabel(select, label, scope) {
    const wanted = canon(label, false);
    if (['aria-label', 'name', 'placeholder'].some(attr => canon(select.getAttribute(attr), false) === wanted)) return true;
    const linked = select.id && scope.querySelector(`label[for="${CSS.escape(select.id)}"]`);
    if (linked && canon(linked.textContent, false) === wanted) return true;
    const previous = select.previousElementSibling;
    return previous?.tagName === 'LABEL' && canon(previous.textContent, false) === wanted;
  }
  function selections(role, scope) {
    if (role.row) return [...scope.querySelectorAll(role.row)].map(row => ({ row, select: row.querySelector('select') }));
    const found = [...scope.querySelectorAll('select')].filter(select => matchesLabel(select, role.label, scope));
    return (role.all ? found : found.slice(0, 1)).map(select => ({ select,
      row: select.closest(`[class*="labeladdon-"]${role.id === '7' ? ', .labeladdon' : ''}`) || select.parentElement
    }));
  }
  // WM-10.02: thumbnail-only derivative; originals and optional large previews never change.
  function thumbnailUrl(original) {
    const delivery = catalog.thumbnailDelivery;
    if (!delivery?.enabled) return original;
    try {
      const url = new URL(original, location.href);
      if (url.hostname !== 'cdn.shopify.com' || !url.pathname.startsWith('/s/files/1/2554/8714/')) return original;
      if (!/\.(?:png|jpe?g|webp)(?:$)/i.test(url.pathname)) return original;
      url.searchParams.set('width', String(delivery.width));
      return url.href;
    } catch (_) { return original; }
  }
  function ensureImage(row, role) {
    let image = row.querySelector('img.' + role.cls);
    if (!image) {
      image = document.createElement('img');
      image.className = role.cls;
      image.alt = role.placeholder ? 'Product / Selected scent' : 'Selected option';
      Object.assign(image.style, style);
      if (role.display) image.style.display = 'block';
      row.insertBefore(image, row.firstChild);
    }
    return image;
  }
  function renderRole(role, scope) {
    selections(role, scope).forEach(({ row, select }) => {
      if (!row) return;
      const text = (select?.value || select?.options?.[select.selectedIndex]?.textContent || '').trim();
      const key = canon(text, role.basic);
      const placeholder = (select?.value ?? '') === '' || /^please select/i.test(text);
      const noMatch = role.id === '2' ? !text || key === 'none' : role.id === '20' ? !text || key === 'none' || key.includes('select') : false;
      const mapped = noMatch ? undefined : role.lookup[key];
      const original = role.placeholder && placeholder ? valueAt(role.placeholder) : mapped || valueAt(role.default);
      if (!original) return;
      const alt = role.fallback && !mapped ? role.fallback : role.placeholder && placeholder ? 'Product' : `${text || 'Default'} – ${role.suffix}`;
      const image = ensureImage(row, role);
      const url = thumbnailUrl(original);
      if (image.getAttribute('src') !== url) image.src = url;
      if (image.alt !== alt) image.alt = alt;
      // A bad derivative falls back once to the original, without touching the catalog.
      image.onerror = url === original ? null : () => { image.onerror = null; image.src = original; };
      const big = role.big && document.getElementById(role.big);
      if (big && big.getAttribute('src') !== original) { big.src = original; big.alt = alt; }
    });
  }
  function signatureHeadings(scope) {
    if (window.PR_IS_CUSTOM_SIGNATURE !== true) return;
    const select = [...scope.querySelectorAll('select')].find(s => matchesLabel(s, 'Signature Floral Bouquet Type', scope));
    const scent = [...scope.querySelectorAll('select')].find(s => matchesLabel(s, 'Signature Floral Diffuser Scent', scope));
    if (!select && !scent) return;
    const row = select?.closest('[class*="labeladdon-"], .labeladdon') || select?.closest('div');
    if (scope.classList) scope.classList.add('pr-custom-signature');
    function heading(target, cls, text) {
      if (!target?.parentNode) return;
      const prev = target.previousElementSibling;
      if (prev?.classList.contains('pr-io-heading') && prev.textContent.trim() === text) return;
      const node = document.createElement('div'); node.className = `pr-io-heading ${cls}`;
      node.textContent = text; target.before(node);
    }
    heading(row, 'pr-io-heading--top', '—  SELECT YOUR BOUQUET —');
    heading(scope.querySelector('.labeladdon-16'), 'pr-io-heading--mid', '—  CUSTOMIZE YOUR SCENT —');
    row?.classList.add('pr-custom-signature-border');
  }
  let scheduled = false;
  let observer;
  function render() {
    scheduled = false;
    // Legacy labeled roles use the first IO container; row-based bundle roles search all rows.
    const scope = document.querySelector('#infiniteoptions-container') || document;
    roles.forEach(role => {
      // WM-06: Special Occasions relocates bouquet fields outside the IO container.
      const relocated = role.relocatedScope && document.querySelector(role.relocatedScope);
      renderRole(role, relocated?.querySelector('select') ? relocated : role.row && role.id !== '6' ? document : scope);
    });
    signatureHeadings(scope);
    watch();
  }
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(render);
  }
  function relevant(node) {
    return node.nodeType === 1 && (node.matches('select, option, label, [class*="labeladdon"], #infiniteoptions-container') ||
      node.querySelector('select, [class*="labeladdon"], #infiniteoptions-container'));
  }
  function watch() {
    observer.disconnect();
    const roots = [...document.querySelectorAll('#infiniteoptions-container'),
      ...roles.flatMap(role => role.relocatedScope ? [...document.querySelectorAll(role.relocatedScope)] : [])];
    if (!roots.length) { observer.observe(document.body, { childList: true, subtree: true }); return; }
    const ancestors = new Set();
    roots.forEach(root => {
      observer.observe(root, { childList: true, subtree: true });
      for (let parent = root.parentElement; parent; parent = parent.parentElement) ancestors.add(parent);
    });
    // Observe replacement along the ancestor chain without watching every body descendant.
    ancestors.forEach(parent => observer.observe(parent, { childList: true }));
    document.querySelectorAll('[class*="labeladdon-"]').forEach(row => {
      if (!row.closest('#infiniteoptions-container')) observer.observe(row, { childList: true, subtree: true });
    });
  }
  observer = new MutationObserver(records => {
    if (records.some(record => [...record.addedNodes, ...record.removedNodes].some(relevant))) schedule();
  });
  // Explicit selections update after the event handlers, without waiting another paint frame.
  // Mutation/replacement work remains frame-batched. Original image URLs are unchanged.
  document.addEventListener('change', event => { if (event.target.matches('select')) queueMicrotask(render); }, true);
  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('shopify:section:unload', schedule);
  window.addEventListener('pagehide', () => observer.disconnect());
  window.addEventListener('pageshow', schedule);
  render();
})();
